import React, { createContext, useContext, useState, useCallback } from 'react';
import { useWorkspace } from "@/context/workspace";

interface OutputInspectorContextValue {
  isOpen: boolean;
  pinnedNodeId: string | null;
  search: string;
  openFor: (nodeId: string) => void;
  unpin: () => void;
  setOpen: (open: boolean) => void;
  setSearch: (query: string) => void;
  reset: () => void;
}

const OutputInspectorContext = createContext<OutputInspectorContextValue | undefined>(undefined);

export function OutputInspectorProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pinnedNodeId, setPinnedNodeId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const openFor = useCallback((nodeId: string) => {
    setPinnedNodeId(nodeId);
    setIsOpen(true);
  }, []);

  const unpin = useCallback(() => {
    setPinnedNodeId(null);
  }, []);

  const setOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // When closing, also clear the pinned node
      setPinnedNodeId(null);
    }
  }, []);

  const updateSearch = useCallback((query: string) => {
    setSearch(query);
  }, []);

  const reset = useCallback(() => {
    setIsOpen(false);
    setPinnedNodeId(null);
    setSearch('');
  }, []);

  // Reset on workspace change
  const { wsChangeCount } = useWorkspace();
  React.useEffect(() => {
    reset();
  }, [wsChangeCount, reset]);

  const value: OutputInspectorContextValue = {
    isOpen,
    pinnedNodeId,
    search,
    openFor,
    unpin,
    setOpen,
    setSearch: updateSearch,
    reset,
  };

  return (
    <OutputInspectorContext.Provider value={value}>
      {children}
    </OutputInspectorContext.Provider>
  );
}

export function useOutputInspector(): OutputInspectorContextValue {
  const ctx = useContext(OutputInspectorContext);
  if (!ctx) throw new Error('useOutputInspector must be used within OutputInspectorProvider');
  return ctx;
}
